<template>
    <form
        class="vrm-custom-widget-form"
        @submit.prevent="onSubmit"
        @reset.prevent="redirectToAdvanced"
    >
        <vrm-text-field
            v-model="widget.title"
            type="text"
            class="sg-element vrm-custom-widget-form__text-field"
            :checks="[MAX_CHARACTERS(100)]"
            :disabled="busy"
            :required="true"
            :title="$t('custom_widgets.widget_name', { i18nFallback: 'Widget name' })"
            data-cy="custom-widget-name-field"
        />

        <div v-if="isAdmin" class="vrm-custom-widget-form__visible-to-all">
            <h2>
                {{
                    $t('custom_widgets.choose_visibility', {
                        i18nFallback: 'Visibility',
                    })
                }}
            </h2>
            <div>
                <div class="vrm-custom-widget-form__visibility_description">
                    {{
                        $t('custom_widgets.visibility_description_short', {
                            i18nFallback:
                                'A widget created by a (restricted) VRM admin can be set to be only visible to you and other (restricted) VRM admins.',
                        })
                    }}
                </div>
                <div
                    v-for="option in radioValues"
                    :key="option.valueEnum"
                    class="vrm-custom-widget-form__radio_buttons"
                    @click="visibilityChanged(option.valueEnum)"
                >
                    <div class="sg-custom-check-and-radio custom-radio">
                        <label class="vrm-custom-widget-form__radio_button_label">
                            <input
                                type="radio"
                                :checked="option.valueEnum === widget.isVisibleToEveryone"
                            />
                            <div class="sg-alternative-display">
                                <div class="sg-outer">
                                    <div class="sg-inner"></div>
                                </div>
                            </div>
                            <div>
                                {{ option.nameEnum }}
                            </div>
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <h2 class="vrm-custom-widget-form__preview-header">
            {{ $t('custom_widgets.preview', { i18nFallback: 'Preview' }) }}
        </h2>

        <graph-preview
            class="vrm-custom-widget-form__preview"
            :widget="widget"
            :installation="installation"
        />

        <div class="vrm-custom-widget-form__add-parameters-block">
            <h1>{{ $t('custom_widgets.add_parameters', { i18nFallback: 'Add parameters' }) }}</h1>
            <p>
                {{
                    $t('custom_widgets.set_up_axis_description_longer', {
                        i18nFallback:
                            'A widget created by a (restricted) VRM admin can be set to be only visible to you and other (restricted) VRM admins.',
                    })
                }}
            </p>

            <div>
                {{
                    $t('y_axis_options_dropdown_title', {
                        i18nFallback: 'Y-axis options',
                    })
                }}
            </div>
            <div class="vrm-custom-widget-form__y-axis-mode-container">
                <vrm-dropdown-field
                    v-model="widget.yAxisProperties.method"
                    class="vrm-custom-widget-form__y-axis-mode-container__select "
                    :title="
                        $t('custom_widgets.customize_y_axis', {
                            i18nFallback: 'Customize the y-axis min and max values and interval',
                        })
                    "
                    :options="localizedYAxisCustomisationOptions"
                    :disabled="busy"
                    data-cy="yaxis-select-field"
                ></vrm-dropdown-field>

                <vrm-axis-configuration-field
                    v-if="widget.yAxisProperties.method.value === ALL_Y_AXIS_SCALES"
                    v-model="widget.yAxisProperties"
                    class="vrm-custom-widget-form__y-axis-mode-container__configuration"
                    :disabled="busy"
                />
            </div>

            <div class="vrm-custom-widget-form__parameter-list">
                <y-axis-parameter-placeholder v-if="widget.parameters.length === 0" />
                <y-axis-parameter
                    v-for="(yAxisParameter, index) in widget.parameters"
                    :key="index"
                    :y-axis-parameter="yAxisParameter"
                    :show-axis-configuration="
                        widget.yAxisProperties.method.value === INDIVIDUAL_Y_AXIS_SCALES
                    "
                    :busy="busy"
                    :open="parameterStates[index]"
                    :options="parameterOptions(yAxisParameter)"
                    @toggleParameter="toggleParameter(index)"
                    @delete="deleteParameter(index)"
                />
            </div>
        </div>

        <div
            :class="[
                'vrm-custom-widget-form__y-axis-mode-container__button',
                { 'button-disabled': widget.parameters.length >= MAX_PARAMETERS },
            ]"
            @click="addNewParameter"
        >
            <span v-if="widget.parameters.length >= MAX_PARAMETERS">
                {{
                    $t('custom_widgets.max_parameters_reached', {
                        i18nFallback: 'Maximum amount (6) of parameters reached',
                    })
                }}
            </span>
            <span v-else>
                <span class="sg-vrm-icon_128_plus-round-solid-16px"></span>
                {{
                    $t('custom_widgets.add_new_parameter', {
                        i18nFallback: 'Add new parameter',
                    })
                }}
            </span>
        </div>

        <div class="sg-btn__container sg-btn__container--align-right">
            <button type="reset" class="sg-btn sg-site" :disabled="busy">
                {{ $t('common.cancel', { i18nFallback: 'Cancel' }) }}
            </button>
            <button type="submit" class="sg-btn sg-blue" :disabled="busy">
                {{ $t('common.save', { i18nFallback: 'Save' }) }}
            </button>
        </div>
    </form>
</template>

<script>
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import ModalCloseHandle from '@/vue/components/modals/ModalCloseHandle';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import VrmToggle from '@/vue/components/shared/toggles/VrmToggle';
import VrmDropdownIcon from '@/vue/components/shared/VrmDropdownIcon';
import { ngInject } from '@/vue/decorators/angular-interop';
import VrmIconButton from '@/vue/components/molecules/VrmIconButton';
import VrmDropdownField from '@/vue/components/forms/VrmDropdownField';
import VrmTextField from '@/vue/components/forms/VrmTextField';
import {
    NOTIFICATION_TYPE_ERROR,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
} from '@/vue/store/modules/notifications';
import {
    ALL_Y_AXIS_SCALES,
    DEFAULT_Y_AXIS_SCALES,
    EnumDefinitions,
    INDIVIDUAL_Y_AXIS_SCALES,
    Y_AXIS_CUSTOMISATION_OPTIONS,
} from '@/enums/custom-widget-axis-customisation';
import GraphPreview from '@/vue/components/shared/custom-widgets/GraphPreview';
import VrmAxisConfigurationField from '@/vue/components/forms/VrmAxisConfigurationField';
import VrmIconDropdownField from '@/vue/components/forms/VrmIconDropdownField';
import YAxisParameter from '@/vue/components/shared/custom-widgets/YAxisParameter.vue';
import { MAX_CHARACTERS } from '@/vue/constants/validation-checks';
import YAxisParameterPlaceholder from '@/vue/components/shared/custom-widgets/YAxisParameterPlaceholder.vue';
import { MONETARY_ATTRIBUTES_CODES } from '@/vue/constants/attribute-types';

@Component({
    methods: { MAX_CHARACTERS },
    components: {
        YAxisParameterPlaceholder,
        YAxisParameter,
        VrmIconDropdownField,
        VrmAxisConfigurationField,
        GraphPreview,
        VrmDropdown,
        VrmDropdownIcon,
        ModalCloseHandle,
        VrmIconButton,
        VrmTextField,
        VrmDropdownField,
        VrmToggle,
    },
})
export default class CustomWidgetForm extends Vue {
    MAX_PARAMETERS = 6;
    INDIVIDUAL_Y_AXIS_SCALES = INDIVIDUAL_Y_AXIS_SCALES;
    ALL_Y_AXIS_SCALES = ALL_Y_AXIS_SCALES;
    Y_AXIS_CUSTOMISATION_METHODS = Y_AXIS_CUSTOMISATION_OPTIONS;

    @Prop()
    idSite;
    @Prop()
    title;
    @Prop()
    save;
    @Prop({ type: Object, default: null })
    currentWidget;

    radioValues = [
        {
            nameEnum: this.$t('custom_widgets.visibility_you_and_other_admins', {
                i18nFallback: 'You and other (restricted) admins',
            }),
            valueEnum: false,
        },
        {
            nameEnum: this.$t('custom_widgets.visibility_all_users_linked_to_site', {
                i18nFallback: 'All users linked to this site',
            }),
            valueEnum: true,
        },
    ];

    @ngInject()
    ApiService;
    @ngInject()
    InstallationsService;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    $rootScope;

    busy = false;
    hasErrors = false;
    installation = null;
    loadedDevices = [];

    // Mirror of the widget.parameters property, containing only the 'open' boolean. This is done
    // to separate actual widget data from view data.
    parameterStates = [];

    widget = {
        title: null,
        isVisibleToEveryone: true,
        parameters: [],
        yAxisProperties: {
            method: this.localizedYAxisCustomisationOptions[DEFAULT_Y_AXIS_SCALES - 1],
            min: null,
            max: null,
            interval: null,
        },
    };

    created() {
        this.getAttributes();
        this.installation = this.InstallationsService.activeInstallation;
        this.widget.isVisibleToEveryone = !this.isReadOnlyAdmin;
    }

    getAttributes() {
        this.busy = true;
        this.ApiService.call2(`/installations/${this.idSite}/attributes`)
            .then(response => {
                this.setDevices(response.data.result);
            })
            .finally(() => {
                if (this.currentWidget !== null && this.currentWidget !== undefined) {
                    this.widget = this.getWidgetData(this.currentWidget);
                    this.parameterStates = Array(this.widget.parameters.length).fill(false, 0);
                } else {
                    this.addNewParameter();
                }
                this.busy = false;
            });
    }

    setDevices(response) {
        const { devices, attributes } = response;
        this.loadedDevices = _.flatMap(devices, device => {
            return _.values(device.instances).map(
                ({ deviceName, instance, customName, ...rest }) => ({
                    ...rest,
                    instance,
                    dataAttributes: this.formatDataAttributes(+rest.idDeviceType, attributes),
                    name:
                        deviceName +
                        (customName ? ' - ' + customName : '') +
                        (instance && +instance !== 0 ? ' [' + instance + ']' : ''),
                }),
            );
        });
    }

    formatDataAttributes(idDeviceType, attributes) {
        return attributes.reduce((accumulator, { description, unit, ...attribute }) => {
            if (idDeviceType !== +attribute.idDeviceType) return accumulator;
            //TODO: improvement; use new formatUnitFactor column of dataAttributes to decide when to use formatWithUnit
            // 1220 = PV Yield Solar Charger Forecast
            // 1538 = PV Yield Solar Charger Forecast v2
            if (attribute.idDataAttribute === 1220 || attribute.idDataAttribute === 1538) {
                unit = 'kWh';
            }
            return [
                ...accumulator,
                {
                    name: unit
                        ? description + ' (' + this.formatUnit(attribute.code, unit) + ')'
                        : description,
                    ...attribute,
                },
            ];
        }, []);
    }

    getWidgetData(widgetData) {
        return {
            title: widgetData.name,
            isVisibleToEveryone: +widgetData.minimumAccessLevel === 0,
            parameters: widgetData.parameters
                ? this.getWidgetParameterData(widgetData.parameters)
                : [],
            yAxisProperties: this.getWidgetYAxisData(widgetData),
        };
    }

    getWidgetYAxisData(yAxisData) {
        return {
            method: this.localizedYAxisCustomisationOptions[
                EnumDefinitions[yAxisData.yAxisScaling ? yAxisData.yAxisScaling : 'DEFAULT'] - 1
            ],
            min: yAxisData.yAxisMin !== undefined ? yAxisData.yAxisMin : null,
            max: yAxisData.yAxisMax !== undefined ? yAxisData.yAxisMax : null,
            interval: yAxisData.yAxisInterval !== undefined ? yAxisData.yAxisInterval : null,
        };
    }

    getWidgetParameterData(parameterData) {
        const parameters = [];
        let missingParameter = false;

        for (const newParameter of parameterData) {
            const parsed = this.parseParameter(newParameter);

            if (parsed === null) {
                missingParameter = true;
            } else {
                parameters.push(parsed);
            }
        }

        if (missingParameter) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_WARNING,
                message: this.$t('custom_widgets.notifications.missing_device'),
            });
        }

        return parameters;
    }

    parseParameter(parameterData) {
        const parsedParameter = {};

        parsedParameter.device = this.loadedDevices.find(
            device =>
                +device.instance === +parameterData.instance &&
                device.dataAttributes.find(
                    attribute => +attribute.idDataAttribute === +parameterData.idDataAttribute,
                ),
        );

        // Device doesn't exist (anymore)
        if (parsedParameter.device === undefined) return null;

        parsedParameter.dataAttribute = parsedParameter.device.dataAttributes.find(
            attribute => +attribute.idDataAttribute === +parameterData.idDataAttribute,
        );

        // Attribute doesn't exist (anymore)
        if (parsedParameter.dataAttribute === undefined) return null;

        parsedParameter.label = this.labelOptions.find(
            label => label.value === parameterData.serieColor,
        );

        parsedParameter.yAxisProperties = {
            min: parameterData.yAxisMin !== undefined ? parameterData.yAxisMin : null,
            max: parameterData.yAxisMax !== undefined ? parameterData.yAxisMax : null,
            interval:
                parameterData.yAxisInterval !== undefined ? parameterData.yAxisInterval : null,
        };

        return parsedParameter;
    }

    getUnusedDataAttributes(selectedDevice) {
        if (!selectedDevice) {
            return [];
        }

        const alreadySelectedAttributes = this.widget.parameters.reduce(
            (accumulator, { device, dataAttribute }) => {
                if (
                    device &&
                    dataAttribute &&
                    device.instance === selectedDevice.instance &&
                    device.idDeviceType === selectedDevice.idDeviceType
                ) {
                    accumulator.push(dataAttribute.idDataAttribute);
                }
                return accumulator;
            },
            [],
        );

        return selectedDevice.dataAttributes.filter(
            a => !alreadySelectedAttributes.includes(a.idDataAttribute),
        );
    }

    getUnusedLabels(selectedLabel) {
        const alreadySelectedLabels = this.widget.parameters.map(
            parameter => parameter.label.value,
        );
        const available = this.labelOptions.filter(
            ({ value }) => !alreadySelectedLabels.includes(value),
        );
        available.push(selectedLabel);
        return available;
    }

    get labelOptions() {
        return [
            { iconClass: 'vrm-custom-widget-form__label--blue', name: '', value: 'blue' },
            { iconClass: 'vrm-custom-widget-form__label--red', name: '', value: 'red' },
            { iconClass: 'vrm-custom-widget-form__label--orange', name: '', value: 'orange' },
            { iconClass: 'vrm-custom-widget-form__label--green', name: '', value: 'green' },
            { iconClass: 'vrm-custom-widget-form__label--black', name: '', value: 'black' },
            { iconClass: 'vrm-custom-widget-form__label--indigo', name: '', value: 'indigo' },
            { iconClass: 'vrm-custom-widget-form__label--magenta', name: '', value: 'magenta' },
        ];
    }

    parameterOptions(parameter) {
        return {
            dataAttributes: this.getUnusedDataAttributes(parameter.device),
            devices: this.loadedDevices,
            labels: this.getUnusedLabels(parameter.label),
        };
    }

    get parameterCount() {
        return this.widget.parameters.filter(p => p.device || p.dataAttribute).length;
    }

    addNewParameter() {
        if (this.widget.parameters.length >= this.MAX_PARAMETERS) return;

        this.widget.parameters.push({
            device: null,
            dataAttribute: null,
            label: this.findUnusedLabel(),
            yAxisProperties: {
                min: null,
                max: null,
                interval: null,
            },
        });

        this.parameterStates.push(false);
        this.toggleParameter(this.widget.parameters.length - 1);
    }

    toggleParameter(parameterIndex) {
        this.parameterStates.splice(parameterIndex, 1, !this.parameterStates[parameterIndex]);
    }

    deleteParameter(index) {
        this.widget.parameters.splice(index, 1);
        this.parameterStates.splice(index, 1);
    }

    get unusedLabels() {
        const usedLabels = this.widget.parameters.map(p => p.label.value);
        return this.labelOptions.filter(l => !usedLabels.includes(l.value));
    }

    findUnusedLabel() {
        if (this.unusedLabels.length === 0) {
            return this.labelOptions[0];
        }
        return this.unusedLabels[0];
    }

    get localizedYAxisCustomisationOptions() {
        return this.Y_AXIS_CUSTOMISATION_METHODS.map(({ name, value }) => {
            return { value, name: this.$t('custom_widgets.y_axis_options.' + name) };
        });
    }

    get payload() {
        const yAxisMethod = this.widget.yAxisProperties.method.value;
        const payload = {
            name: this.widget.title,
            onlyAdmins: this.widget.isVisibleToEveryone ? 0 : 1,
            yAxisScaling: yAxisMethod,
        };

        if (yAxisMethod === ALL_Y_AXIS_SCALES) {
            payload.yAxisProperties = {
                globalMin: this.widget.yAxisProperties.min,
                globalMax: this.widget.yAxisProperties.max,
                globalInterval: this.widget.yAxisProperties.interval,
            };
        }

        payload.parameters = this.widget.parameters.map(p => ({
            dataAttribute: p.dataAttribute.idDataAttribute,
            serieColor: p.label.value,
            instance: p.device.instance,
            yAxisProperties:
                yAxisMethod === INDIVIDUAL_Y_AXIS_SCALES ? p.yAxisProperties : undefined,
        }));

        return payload;
    }

    async onSubmit() {
        if (this.busy || !this.validate()) return;
        this.busy = true;
        try {
            const { data } = await this.save(this.payload);
            if (data && data.success) {
                this.redirectToAdvanced();
                this.vrmNotificationsService.add({
                    type: NOTIFICATION_TYPE_SUCCESS,
                    message: this.$t(
                        this.currentWidget
                            ? 'custom_widgets.notifications.edit_success'
                            : 'custom_widgets.notifications.create_success',
                    ),
                });
            }
        } catch (e) {
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_ERROR,
                message: this.$t(
                    this.currentWidget
                        ? 'custom_widgets.notifications.edit_failed'
                        : 'custom_widgets.notifications.create_failed',
                ),
            });
        } finally {
            this.busy = false;
        }
    }

    validate() {
        this.hasErrors = false;
        this.validateChild(this);
        return !this.hasErrors;
    }

    validateChild(component) {
        if ('vrmValidate' in component) {
            const valid = component.vrmValidate();
            if (!valid) {
                this.hasErrors = true;
            }
        } else {
            component.$children.forEach(child => this.validateChild(child));
        }
    }

    redirectToAdvanced() {
        this.$router.push('base.installation.advanced', { id: this.idSite });
    }

    formatUnit(code, unit) {
        if (!MONETARY_ATTRIBUTES_CODES.find(c => c === code)) return unit;
        return unit.replace('mu', this.installation.currency.sign);
    }

    visibilityChanged(newVisibility) {
        this.widget.isVisibleToEveryone = newVisibility;
    }

    get isAdmin() {
        return this.$rootScope.userState.isAdmin;
    }

    get isReadOnlyAdmin() {
        return this.isAdmin && !this.installation.view_permissions.update_settings;
    }
}
</script>
